import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { ConnectivityService } from '@core/services/connectivity.service';

@Injectable({ providedIn: 'root' })
export class PullToRefreshService {
  private readonly document = inject(DOCUMENT);
  private readonly connectivityService = inject(ConnectivityService);
  private readonly activationThreshold = 84;
  private readonly pullStartThreshold = 18;
  private readonly maxPullDistance = 132;
  private readonly pullResistance = 0.42;
  private readonly topTolerance = 2;
  private initialized = false;
  private startY = 0;
  private tracking = false;
  private activeScroller: HTMLElement | null = null;

  readonly pullDistance = signal(0);
  readonly isRefreshing = signal(false);
  readonly isPulling = computed(
    () => this.pullDistance() > 0 || this.isRefreshing(),
  );
  readonly isArmed = computed(
    () => this.pullDistance() >= this.activationThreshold,
  );

  init(): void {
    if (this.initialized || typeof window === 'undefined') {
      return;
    }

    this.initialized = true;
    window.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    window.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    window.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', this.handleTouchCancel, { passive: true });
  }

  private readonly handleTouchStart = (event: TouchEvent): void => {
    if (this.isRefreshing() || event.touches.length !== 1) {
      this.resetTracking();
      return;
    }

    const scroller = this.resolveScrollableContainer(event.target);
    if (!scroller || scroller.scrollTop > this.topTolerance) {
      this.resetTracking();
      return;
    }

    this.tracking = true;
    this.activeScroller = scroller;
    this.startY = event.touches[0]?.clientY ?? 0;
  };

  private readonly handleTouchMove = (event: TouchEvent): void => {
    if (!this.tracking || !this.activeScroller || event.touches.length !== 1) {
      return;
    }

    if (this.activeScroller.scrollTop > this.topTolerance) {
      this.resetTracking();
      return;
    }

    const currentY = event.touches[0]?.clientY ?? 0;
    const deltaY = currentY - this.startY;

    if (deltaY <= this.pullStartThreshold) {
      this.pullDistance.set(0);
      return;
    }

    const nextDistance = Math.min(
      this.maxPullDistance,
      (deltaY - this.pullStartThreshold) * this.pullResistance,
    );
    this.pullDistance.set(nextDistance);
    event.preventDefault();
  };

  private readonly handleTouchEnd = (): void => {
    if (!this.tracking) {
      return;
    }

    const shouldRefresh = this.pullDistance() >= this.activationThreshold;
    this.resetTracking();

    if (!shouldRefresh) {
      this.pullDistance.set(0);
      return;
    }

    if (!this.connectivityService.isOnline()) {
      this.pullDistance.set(0);
      this.isRefreshing.set(false);
      return;
    }

    this.isRefreshing.set(true);
    this.pullDistance.set(this.activationThreshold * 0.55);

    window.setTimeout(() => {
      window.location.reload();
    }, 60);
  };

  private readonly handleTouchCancel = (): void => {
    this.resetTracking();
    this.pullDistance.set(0);
  };

  private resolveScrollableContainer(target: EventTarget | null): HTMLElement | null {
    const targetElement =
      typeof Element !== 'undefined' && target instanceof Element
        ? target
        : null;
    const container = targetElement?.closest?.(
      '[data-pull-refresh-container]',
    ) as HTMLElement | null;

    if (container) {
      return this.canScrollVertically(container) ? container : null;
    }

    const documentScroller =
      (this.document.scrollingElement as HTMLElement | null) ??
      (this.document.documentElement as HTMLElement | null) ??
      null;

    return documentScroller && this.canScrollVertically(documentScroller)
      ? documentScroller
      : null;
  }

  private resetTracking(): void {
    this.tracking = false;
    this.activeScroller = null;
    this.startY = 0;
  }

  private canScrollVertically(element: HTMLElement): boolean {
    return element.scrollHeight > element.clientHeight + this.topTolerance;
  }
}
